import React, { useState, useRef, useEffect } from 'react';
import Select from 'react-select';
import axios from 'axios';
import './addReportModal.css';
import LoadingPopup from '../../../SharedView/LoadingPopup';

const reportOptions = [
    { value: 'before_cleaning_video', label: 'Before Cleaning Video' },
    { value: 'after_cleaning_video', label: 'After Cleaning Video' },
    { value: 'stained_laundry_pickup', label: 'Stained Laundry Pickup' },
    { value: 'damages', label: 'Damages' },
    { value: 'apartment_condition', label: 'Apartment Condition' },
    { value: 'task_report', label: 'Task Report' },
];

function truncateText(str, max = 60) {
    const s = String(str || '').trim();
    if (!s) return '';
    return s.length <= max ? s : `${s.slice(0, max - 3)}...`;
}

function AddReportModal({ bookingId, onClose }) {
    const [selectedType, setSelectedType] = useState(null);
    const [comment, setComment] = useState('');
    const [files, setFiles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [feedbackMessage, setFeedbackMessage] = useState('');
    const [fileProgress, setFileProgress] = useState({});

    // Booking tasks so a "Task Report" can be linked to the exact task —
    // numbered in creation order, matching the Tasks section and the apps.
    const [tasks, setTasks] = useState([]);
    const [selectedTask, setSelectedTask] = useState(null);

    useEffect(() => {
        let stale = false;
        axios.get(`/api/tasks/booking/${bookingId}`)
            .then((res) => { if (!stale) setTasks(Array.isArray(res.data) ? res.data : []); })
            .catch(() => { if (!stale) setTasks([]); });
        return () => { stale = true; };
    }, [bookingId]);

    const taskOptions = [
        { value: null, label: '— Not linked to a task —' },
        ...tasks.map((t, i) => ({ value: t.id, label: `${i + 1}. ${truncateText(t.instruction) || `Task #${t.id}`}` })),
    ];

    const fileInputRef = useRef(null);

    const handleFileChange = (e) => {
        const selected = Array.from(e.target.files);
        setFiles(prev => [...prev, ...selected]);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        const droppedFiles = Array.from(e.dataTransfer.files);
        setFiles(prev => [...prev, ...droppedFiles]);
    };

    // ADD: multipart uploader (inside AddReportModal component)
    const uploadFileMultipart = async (file, bookingId) => {
        const PART_SIZE = 8 * 1024 * 1024; // >=5MB except last part
        const MAX_RETRIES = 3;
        const BASE_BACKOFF_MS = 500;
        const safeType = file.type || 'application/octet-stream';

        // start progress at 0 for this file
        setFileProgress(prev => ({ ...prev, [file.name]: 0 }));

        // 1) INIT — v2 (AWS S3 Mumbai) FIRST: same-region-ish uploads for the PH
        // admins instead of Frankfurt. ANY v2 init failure (flag off, old server,
        // creds) silently falls back to the untouched v1/DO rail.
        let rail = 'v1';
        let uploadId, key, fileUrl;
        try {
            const { data: v2init } = await axios.post('/api/uploads/v2/multipart-init', {
                kind: 'report-media',
                booking_id: bookingId,
                file_name: file.name,
                content_type: safeType,
            });
            ({ uploadId, key } = v2init);
            fileUrl = v2init.file_url;
            rail = 'v2';
        } catch (v2Err) {
            const { data: init } = await axios.post('/api/booking-reports/multipart-init', {
                booking_id: bookingId,
                fileName: file.name,
                fileType: safeType,
            });
            ({ uploadId, key, fileUrl } = init);
        }
        const PRESIGN_URL = rail === 'v2' ? '/api/uploads/v2/multipart-presign' : '/api/booking-reports/multipart-presign';
        // v2 completion needs each part's ETag (the buckets expose it via CORS).
        const etagByPart = {};

        const totalBytes = file.size;

        const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

        try {
            // 2) Upload parts with a small pool — a single stream leaves most of a
            // high-latency link idle (PLAN_upload_speed P0). Per-part progress is
            // summed across in-flight parts so the bar stays truthful.
            const PART_CONCURRENCY = 4;
            const parts = [];
            for (let start = 0, pn = 1; start < file.size; start += PART_SIZE, pn++) {
                parts.push({ partNumber: pn, start, end: Math.min(start + PART_SIZE, file.size) });
            }
            const loadedByPart = {};
            const reportProgress = () => {
                const current = Object.values(loadedByPart).reduce((a, b) => a + b, 0);
                const percent = Math.min(100, Math.round((current / totalBytes) * 100));
                setFileProgress(prev => ({ ...prev, [file.name]: percent }));
            };
            let nextPartIdx = 0;
            let poolErr = null;
            const partWorker = async () => {
                for (;;) {
                    if (poolErr) return;
                    const idx = nextPartIdx++;
                    if (idx >= parts.length) return;
                    const { partNumber, start, end } = parts[idx];
                    const blobPart = file.slice(start, end);
                    const partSize = blobPart.size;
                    let attempt = 0;
                    let uploaded = false;
                    while (attempt < MAX_RETRIES && !uploaded) {
                        try {
                            const { data: presign } = await axios.post(PRESIGN_URL, {
                                key,
                                uploadId,
                                partNumber,
                            });
                            const putRes = await axios.put(presign.url, blobPart, {
                                withCredentials: false,
                                onUploadProgress: (evt) => {
                                    loadedByPart[partNumber] = Math.min(evt.loaded || 0, partSize);
                                    reportProgress();
                                },
                            });
                            if (rail === 'v2') {
                                const etag = putRes.headers?.etag || putRes.headers?.ETag;
                                if (!etag) throw new Error(`Part ${partNumber} uploaded but no ETag was exposed`);
                                etagByPart[partNumber] = etag;
                            }
                            uploaded = true;
                            loadedByPart[partNumber] = partSize;
                            reportProgress();
                        } catch (e) {
                            attempt += 1;
                            loadedByPart[partNumber] = 0;
                            if (attempt >= MAX_RETRIES) {
                                poolErr = e;
                                throw e; // bubble out to abort
                            }
                            await sleep(BASE_BACKOFF_MS * Math.pow(2, attempt - 1));
                        }
                    }
                }
            };
            {
                const workerCount = Math.max(1, Math.min(PART_CONCURRENCY, parts.length));
                const results = await Promise.allSettled(Array.from({ length: workerCount }, partWorker));
                const rejected = results.find((r) => r.status === 'rejected');
                if (rejected) throw rejected.reason;
            }
            // 3) COMPLETE — v2 supplies the ETag list; v1 lets the server list parts.
            let completedKey, completedUrl;
            if (rail === 'v2') {
                const partsList = Object.keys(etagByPart).map(Number).sort((a, b) => a - b)
                    .map((pn) => ({ PartNumber: pn, ETag: etagByPart[pn] }));
                const { data: complete } = await axios.post('/api/uploads/v2/multipart-complete', { key, uploadId, parts: partsList });
                completedKey = complete.key || key;
                completedUrl = complete.file_url || fileUrl;
            } else {
                const { data: complete } = await axios.post('/api/booking-reports/multipart-complete', { key, uploadId });
                completedKey = complete.fileKey || key;
                completedUrl = complete.fileUrl || fileUrl;
            }

            // ensure it lands at 100
            setFileProgress(prev => ({ ...prev, [file.name]: 100 }));

            return {
                fileKey: completedKey,
                fileUrl: completedUrl,
            };
        } catch (err) {
            try { await axios.post('/api/booking-reports/multipart-abort', { key, uploadId }); } catch (_) { }
            // leave the last known %; optionally set error sentinel here
            throw err;
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!selectedType) return alert("Please select a report type.");
        if (files.length === 0) return alert("Please attach at least one file.");

        try {
            setLoading(true);

            // 1) Upload each file with multipart (sequential; shows per-file progress)
            const uploaded = [];
            for (const file of files) {
                // initialize 0% (in case not set yet)
                setFileProgress(prev => ({ ...prev, [file.name]: 0 }));
                const { fileKey, fileUrl } = await uploadFileMultipart(file, bookingId);
                uploaded.push({ fileKey, fileUrl });
            }


            // 2) Bulk-save all uploaded files as booking reports
            await axios.post('/api/booking-reports/upload-report-multipart', {
                booking_id: bookingId,
                type: selectedType.value,
                task_id: selectedType.value === 'task_report' ? (selectedTask?.value ?? null) : null,
                comment: comment || null,
                files: uploaded,
            });

            setFeedbackMessage("Report added successfully!");
        } catch (error) {
            console.error("Error uploading report (multipart):", error);
            setFeedbackMessage("Failed to add report.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="popup-overlay">
            <div className="add-report-popup">
                <button type="button" className="modal-x" onClick={onClose}>&times;</button>
                <h2>Add Report</h2>
                <form onSubmit={handleSubmit} className="booking-report-form">
                    <div className="form-group">
                        <label>Report Type:</label>
                        <Select
                            options={reportOptions}
                            value={selectedType}
                            onChange={(opt) => {
                                setSelectedType(opt);
                                // a task link only makes sense on a task report
                                if (opt?.value !== 'task_report') setSelectedTask(null);
                            }}
                            placeholder="Select report type..."
                        />
                    </div>

                    {selectedType?.value === 'task_report' && (
                        <div className="form-group">
                            <label>Task:</label>
                            <Select
                                options={taskOptions}
                                value={selectedTask ?? taskOptions[0]}
                                onChange={(opt) => setSelectedTask(opt?.value != null ? opt : null)}
                                placeholder="Select the task…"
                            />
                        </div>
                    )}

                    <div className="form-group">
                        <label>Comment:</label>
                        <textarea
                            value={comment}
                            onChange={(e) => setComment(e.target.value)}
                            placeholder="Enter comment..."
                            className="textarea-field"
                        />
                    </div>

                    <div
                        className="drop-zone"
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={handleDrop}
                        onClick={() => fileInputRef.current.click()}
                    >
                        <p>Drag & drop files here or click to browse</p>
                        <input
                            ref={fileInputRef}
                            type="file"
                            multiple
                            accept="image/*,video/*"
                            onChange={handleFileChange}
                            className="file-input"
                            style={{ display: 'none' }}
                        />
                    </div>

                    {files.length > 0 && (
                        <ul className="file-list">
                            {files.map((f, i) => (
                                <li key={i}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <span>{f.name}</span>
                                        {typeof fileProgress[f.name] === 'number' && (
                                            <span style={{ marginLeft: 8, minWidth: 48, textAlign: 'right' }}>
                                                {fileProgress[f.name]}%
                                            </span>
                                        )}
                                    </div>
                                    {typeof fileProgress[f.name] === 'number' && (
                                        <div style={{ height: 6, background: '#eee', borderRadius: 4, overflow: 'hidden', marginTop: 6 }}>
                                            <div
                                                style={{
                                                    width: `${fileProgress[f.name]}%`,
                                                    height: '100%',
                                                    background: '#4caf50',
                                                    transition: 'width 120ms linear',
                                                }}
                                            />
                                        </div>
                                    )}
                                </li>
                            ))}
                        </ul>
                    )}

                    <button type="submit" disabled={loading} className="submit-button">
                        {loading ? "Uploading..." : "Submit Report"}
                    </button>
                </form>

                <LoadingPopup
                    isLoading={loading}
                    feedbackMessage={feedbackMessage}
                    onClose={() => {
                        setFeedbackMessage('');
                        if (feedbackMessage === 'Report added successfully!') {
                            onClose();
                        }
                    }}
                />
            </div>
        </div>
    );
}

export default AddReportModal;
